import React, { useEffect, useState } from "react";
import { listar, actualizar } from "../../../services/modulosSistemaService";
import { showToast } from "../../../utils/showToast";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../styles/dashboardTheme.css";
import "../../../styles/managementTheme.css";

const fechaLocal = (iso) => {
  if (!iso) return "";
  return String(iso).substring(0, 10);
};

const estaVencido = (fecha) => {
  if (!fecha) return false;
  return new Date(fecha) < new Date();
};

function ModalEditar({ modulo, onClose, onGuardado }) {
  const [habilitado,       setHabilitado]       = useState(!!modulo.habilitado);
  const [fechaExpiracion,  setFechaExpiracion]  = useState(fechaLocal(modulo.fecha_expiracion));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await actualizar(modulo.clave, {
        habilitado,
        fecha_expiracion: fechaExpiracion || null,
      });
      showToast({ type: "success", message: `Módulo "${modulo.nombre}" actualizado.` });
      onGuardado(updated.modulo ?? { ...modulo, habilitado, fecha_expiracion: fechaExpiracion || null });
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al actualizar el módulo." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1"
      style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className={`bi ${modulo.icono || "bi-puzzle"} me-2`} />
                {modulo.nombre}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {modulo.descripcion && (
                <p className="text-muted mb-3" style={{ fontSize: 13 }}>{modulo.descripcion}</p>
              )}

              {/* Toggle habilitado */}
              <div className="d-flex align-items-center justify-content-between p-3 rounded mb-3"
                style={{ background: habilitado ? "var(--bs-success-bg-subtle)" : "var(--bs-secondary-bg-subtle)" }}>
                <div>
                  <div className="fw-semibold">{habilitado ? "Módulo activo" : "Módulo inactivo"}</div>
                  <small className="text-muted">
                    {habilitado ? "Los usuarios con permiso pueden acceder." : "Ningún usuario puede acceder, sin importar sus permisos."}
                  </small>
                </div>
                <div className="form-check form-switch mb-0 ms-3">
                  <input className="form-check-input" type="checkbox" role="switch"
                    style={{ width: 48, height: 24 }}
                    checked={habilitado} onChange={e => setHabilitado(e.target.checked)} />
                </div>
              </div>

              {/* Fecha de expiración */}
              <div>
                <label className="form-label">Fecha de expiración
                  <span className="text-muted ms-2" style={{ fontSize: 12 }}>(opcional — licencia por período)</span>
                </label>
                <input type="date" className="form-control"
                  value={fechaExpiracion}
                  onChange={e => setFechaExpiracion(e.target.value)} />
                {fechaExpiracion && estaVencido(fechaExpiracion) && (
                  <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                    <i className="bi bi-exclamation-triangle me-1" />
                    Esta fecha ya venció — el módulo se desactivará automáticamente.
                  </div>
                )}
                {fechaExpiracion && !estaVencido(fechaExpiracion) && (
                  <div className="text-success mt-1" style={{ fontSize: 12 }}>
                    <i className="bi bi-calendar-check me-1" />
                    Activo hasta el {fechaExpiracion}.
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</>
                  : "Guardar cambios"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TarjetaModulo({ modulo, onEditar }) {
  const vencido   = estaVencido(modulo.fecha_expiracion);
  const activo    = modulo.habilitado && !vencido;
  const expira    = modulo.habilitado && modulo.fecha_expiracion && !vencido;

  return (
    <div className={`management-card h-100 d-flex flex-column ${activo ? "border-success border-start border-3" : "border-secondary border-start border-3"}`}>
      <div className="d-flex align-items-start justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0
            ${activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
            style={{ width: 42, height: 42 }}>
            <i className={`bi ${modulo.icono || "bi-puzzle"} fs-5`} />
          </div>
          <div>
            <div className="fw-semibold">{modulo.nombre}</div>
            <small className="text-muted font-monospace">{modulo.clave}</small>
          </div>
        </div>
        <span className={`badge ${activo ? "bg-success" : vencido ? "bg-danger" : "bg-secondary"}`}>
          {activo ? "Activo" : vencido ? "Vencido" : "Inactivo"}
        </span>
      </div>

      {modulo.descripcion && (
        <p className="text-muted mb-3 flex-grow-1" style={{ fontSize: 13 }}>{modulo.descripcion}</p>
      )}

      {expira && (
        <div className="d-flex align-items-center gap-1 mb-2 text-warning" style={{ fontSize: 12 }}>
          <i className="bi bi-clock" />
          Expira: {fechaLocal(modulo.fecha_expiracion)}
        </div>
      )}
      {vencido && (
        <div className="d-flex align-items-center gap-1 mb-2 text-danger" style={{ fontSize: 12 }}>
          <i className="bi bi-exclamation-triangle" />
          Venció el {fechaLocal(modulo.fecha_expiracion)}
        </div>
      )}

      <button
        type="button"
        className="btn btn-outline-secondary btn-sm mt-auto"
        onClick={() => onEditar(modulo)}
      >
        <i className="bi bi-pencil me-2" />
        Configurar
      </button>
    </div>
  );
}

export default function ModulosSistemaView() {
  const [modulos,  setModulos]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editando, setEditando] = useState(null);

  useEffect(() => {
    listar()
      .then(data => setModulos(Array.isArray(data) ? data : []))
      .catch(() => showToast({ type: "danger", message: "No se pudieron cargar los módulos." }))
      .finally(() => setLoading(false));
  }, []);

  const handleGuardado = (moduloActualizado) => {
    setModulos(prev => prev.map(m =>
      m.clave === moduloActualizado.clave ? { ...m, ...moduloActualizado } : m
    ));
  };

  const activos  = modulos.filter(m => m.habilitado && !estaVencido(m.fecha_expiracion)).length;
  const vencidos = modulos.filter(m => estaVencido(m.fecha_expiracion)).length;

  return (
    <section className="management-page management-theme-blue">
      {/* Header */}
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Seguridad / Módulos</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-toggles" />
            Módulos del sistema
          </h4>
          <p className="text-muted mb-0">
            Activa o desactiva funcionalidades según el plan comercial de cada cliente.
          </p>
        </div>
      </div>

      {/* Resumen */}
      <div className="row g-3 mb-4">
        {[
          { label: "Total módulos",   value: modulos.length, color: "primary",   icon: "bi-puzzle" },
          { label: "Activos",         value: activos,        color: "success",   icon: "bi-check-circle" },
          { label: "Inactivos",       value: modulos.length - activos - vencidos, color: "secondary", icon: "bi-dash-circle" },
          { label: "Vencidos",        value: vencidos,       color: "danger",    icon: "bi-exclamation-triangle" },
        ].map(({ label, value, color, icon }) => (
          <div className="col-6 col-md-3" key={label}>
            <div className={`management-card border-${color} border-start border-3 py-3`}>
              <div className="d-flex align-items-center gap-2">
                <i className={`bi ${icon} text-${color} fs-4`} />
                <div>
                  <div className="fw-bold fs-5">{value}</div>
                  <small className="text-muted">{label}</small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tarjetas de módulos */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : modulos.length === 0 ? (
        <div className="management-card text-center py-5 text-muted">
          <i className="bi bi-puzzle fs-1 d-block mb-2 opacity-25" />
          No hay módulos configurados.
        </div>
      ) : (
        <div className="row g-3">
          {modulos.map(m => (
            <div className="col-md-6 col-lg-4" key={m.clave}>
              <TarjetaModulo modulo={m} onEditar={setEditando} />
            </div>
          ))}
        </div>
      )}

      {editando && (
        <ModalEditar
          modulo={editando}
          onClose={() => setEditando(null)}
          onGuardado={handleGuardado}
        />
      )}
    </section>
  );
}
